import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Keyboard, Pencil, RotateCcw } from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  useKeyboardShortcuts,
  formatShortcutKeys,
  eventToKeyString,
  type ShortcutCategory,
  type KeyboardShortcut,
} from '@/contexts/KeyboardShortcutContext';

const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  general: 'General',
  navigation: 'Navigation',
  search: 'Search',
  email: 'Email & Inbox',
  activities: 'Activities & Tasks',
  marketing: 'Marketing',
  sales: 'Sales & Pipeline',
  communications: 'Communications',
};

const CATEGORY_ORDER: ShortcutCategory[] = [
  'general',
  'navigation',
  'search',
  'email',
  'activities',
  'marketing',
  'sales',
  'communications',
];

/** Inline editor for remapping a single shortcut */
const ShortcutEditor: React.FC<{
  shortcut: KeyboardShortcut;
  onSave: (id: string, newKeys: string) => void;
  onReset: (id: string) => void;
  onCancel: () => void;
}> = ({ shortcut, onSave, onReset, onCancel }) => {
  const [recording, setRecording] = useState(false);
  const [pendingKeys, setPendingKeys] = useState<string | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const startRecording = useCallback(() => {
    setRecording(true);
    setPendingKeys(null);
  }, []);

  useEffect(() => {
    if (!recording) return;
    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setRecording(false);
        setPendingKeys(null);
        return;
      }
      const keyStr = eventToKeyString(e);
      if (keyStr) {
        setPendingKeys(keyStr);
        setRecording(false);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [recording]);

  useEffect(() => {
    if (recording && inputRef.current) {
      inputRef.current.focus();
    }
  }, [recording]);

  const isCustomized = shortcut.keys !== shortcut.defaultKeys;
  const displayKeys = pendingKeys ?? shortcut.keys;

  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{shortcut.label}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {recording ? (
          <div
            ref={inputRef}
            tabIndex={0}
            className="inline-flex items-center justify-center min-w-[80px] h-8 px-3 bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-400 dark:border-yellow-600 rounded-md text-xs font-mono text-yellow-700 dark:text-yellow-300 animate-pulse"
          >
            Press keys...
          </div>
        ) : (
          <kbd className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 border rounded-md text-xs font-mono shadow-sm ${
            pendingKeys
              ? 'bg-green-100 dark:bg-green-900/30 border-green-400 dark:border-green-600 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
          }`}>
            {formatShortcutKeys(displayKeys)}
          </kbd>
        )}

        {!recording && !pendingKeys && (
          <button
            onClick={startRecording}
            className="p-1.5 text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/50 rounded transition-colors"
            title="Record new shortcut"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}

        {pendingKeys && (
          <>
            <button
              onClick={() => { onSave(shortcut.id, pendingKeys); setPendingKeys(null); }}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => setPendingKeys(null)}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          </>
        )}

        {!recording && !pendingKeys && isCustomized && (
          <button
            onClick={() => onReset(shortcut.id)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title={`Reset to default (${formatShortcutKeys(shortcut.defaultKeys)})`}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}

        {!recording && !pendingKeys && (
          <button
            onClick={onCancel}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="Done editing"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export const KeyboardShortcutsHelp: React.FC = () => {
  const {
    isHelpOpen,
    setIsHelpOpen,
    getShortcutsByCategory,
    customizeShortcut,
    resetShortcut,
    resetAllShortcuts,
    getCustomOverrides,
  } = useKeyboardShortcuts();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isHelpOpen) {
      setEditingId(null);
      return;
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (editingId) {
          setEditingId(null);
        } else {
          setIsHelpOpen(false);
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isHelpOpen, setIsHelpOpen, editingId]);

  useEffect(() => {
    if (isHelpOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isHelpOpen]);

  if (!isHelpOpen) return null;

  const grouped = getShortcutsByCategory();
  const hasOverrides = Object.keys(getCustomOverrides()).length > 0;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={() => { if (!editingId) setIsHelpOpen(false); }}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Keyboard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Keyboard Shortcuts</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Click any shortcut to remap it</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasOverrides && (
              <button
                onClick={resetAllShortcuts}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Reset all shortcuts to defaults"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset all
              </button>
            )}
            <button
              onClick={() => setIsHelpOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-140px)] px-6 py-4">
          <div className="space-y-6">
            {CATEGORY_ORDER.map(category => {
              const items = grouped[category];
              if (!items || items.length === 0) return null;

              return (
                <div key={category}>
                  <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                    {CATEGORY_LABELS[category]}
                  </h3>
                  <div className="space-y-1">
                    {items.map(shortcut => {
                      if (editingId === shortcut.id) {
                        return (
                          <ShortcutEditor
                            key={shortcut.id}
                            shortcut={shortcut}
                            onSave={(id, newKeys) => {
                              customizeShortcut(id, newKeys);
                              setEditingId(null);
                            }}
                            onReset={(id) => {
                              resetShortcut(id);
                              setEditingId(null);
                            }}
                            onCancel={() => setEditingId(null)}
                          />
                        );
                      }

                      const isCustomized = shortcut.keys !== shortcut.defaultKeys;

                      return (
                        <div
                          key={shortcut.id}
                          className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer group"
                          onClick={() => setEditingId(shortcut.id)}
                          title="Click to remap this shortcut"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-700 dark:text-gray-300">{shortcut.label}</span>
                            {shortcut.description && shortcut.description !== shortcut.label && (
                              <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{shortcut.description}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            <kbd className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 border rounded-md text-xs font-mono shadow-sm ${
                              isCustomized
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                                : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                            }`}>
                              {formatShortcutKeys(shortcut.keys)}
                            </kbd>
                            <Pencil className="h-3 w-3 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
            <span>Press <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">?</kbd> to toggle &middot; Click any shortcut to remap</span>
            <span>Press <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Esc</kbd> to close</span>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

/** Floating button to open keyboard shortcuts help */
export const KeyboardShortcutsButton: React.FC = () => {
  const { setIsHelpOpen } = useKeyboardShortcuts();

  return (
    <button
      onClick={() => setIsHelpOpen(true)}
      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
      title="Keyboard shortcuts (?)"
    >
      <Keyboard className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Shortcuts</span>
      <kbd className="hidden sm:inline-flex items-center px-1 py-0.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-[10px] font-mono">?</kbd>
    </button>
  );
};

export default KeyboardShortcutsHelp;
